import { useMemo, useState } from 'react'
import { Check, Copy, Download, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from '../../lib/i18n'

/**
 * Fully client-side llms.txt generator widget.
 *
 * Builds a spec-correct llms.txt (H1 + blockquote summary + optional H2 link
 * sections with `[title](url): description` bullets) from a small form, with
 * copy and download. No network, no state leaves the browser. Mounted by the
 * tool detail page via the `llms-txt-generator` toolKey discriminant. All copy
 * is read from the UI dictionary so it renders natively in EN and DE.
 */

type LinkRow = { title: string; url: string; description: string }

const MAX_LINKS = 12

function buildLlmsTxt(input: {
  name: string
  summary: string
  details: string
  sectionTitle: string
  links: LinkRow[]
}): string {
  const lines: string[] = []
  lines.push(`# ${input.name.trim() || 'Your Site Name'}`)
  lines.push('')
  if (input.summary.trim()) {
    lines.push(`> ${input.summary.trim()}`)
    lines.push('')
  }
  if (input.details.trim()) {
    lines.push(input.details.trim())
    lines.push('')
  }
  const validLinks = input.links.filter(
    (link) => link.title.trim() && link.url.trim(),
  )
  if (validLinks.length) {
    lines.push(`## ${input.sectionTitle.trim() || 'Docs'}`)
    for (const link of validLinks) {
      const desc = link.description.trim()
      lines.push(
        `- [${link.title.trim()}](${link.url.trim()})${desc ? `: ${desc}` : ''}`,
      )
    }
    lines.push('')
  }
  return `${lines.join('\n').trimEnd()}\n`
}

export function LlmsTxtGenerator() {
  const { dict } = useTranslation()
  const ui = dict.pages.toolWidgets.llmsTxt

  const [name, setName] = useState('')
  const [summary, setSummary] = useState('')
  const [details, setDetails] = useState('')
  const [sectionTitle, setSectionTitle] = useState('Docs')
  const [links, setLinks] = useState<LinkRow[]>([
    { title: '', url: '', description: '' },
  ])
  const [copied, setCopied] = useState(false)

  const output = useMemo(
    () => buildLlmsTxt({ name, summary, details, sectionTitle, links }),
    [name, summary, details, sectionTitle, links],
  )

  const updateLink = (index: number, patch: Partial<LinkRow>) => {
    setLinks((prev) =>
      prev.map((link, i) => (i === index ? { ...link, ...patch } : link)),
    )
  }

  const addLink = () => {
    setLinks((prev) =>
      prev.length >= MAX_LINKS
        ? prev
        : [...prev, { title: '', url: '', description: '' }],
    )
  }

  const removeLink = (index: number) => {
    setLinks((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    )
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  const download = () => {
    const blob = new Blob([output], { type: 'text/plain;charset=utf-8' })
    const href = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = href
    anchor.download = 'llms.txt'
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(href)
  }

  return (
    <div className="tool-widget">
      <form className="tool-widget-form" onSubmit={(e) => e.preventDefault()}>
        <label className="tool-field">
          <span>{ui.nameLabel}</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={ui.namePlaceholder}
          />
        </label>
        <label className="tool-field">
          <span>{ui.summaryLabel}</span>
          <input
            type="text"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder={ui.summaryPlaceholder}
          />
        </label>
        <label className="tool-field">
          <span>{ui.detailsLabel}</span>
          <textarea
            rows={2}
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={ui.detailsPlaceholder}
          />
        </label>
        <label className="tool-field">
          <span>{ui.sectionLabel}</span>
          <input
            type="text"
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            placeholder={ui.sectionPlaceholder}
          />
        </label>

        <fieldset className="tool-links">
          <legend>{ui.linksLabel}</legend>
          {links.map((link, index) => (
            <div className="tool-link-row" key={index}>
              <input
                type="text"
                value={link.title}
                onChange={(e) => updateLink(index, { title: e.target.value })}
                placeholder={ui.linkTitlePlaceholder}
                aria-label={ui.linkTitlePlaceholder}
              />
              <input
                type="url"
                value={link.url}
                onChange={(e) => updateLink(index, { url: e.target.value })}
                placeholder={ui.linkUrlPlaceholder}
                aria-label={ui.linkUrlPlaceholder}
              />
              <input
                type="text"
                value={link.description}
                onChange={(e) =>
                  updateLink(index, { description: e.target.value })
                }
                placeholder={ui.linkDescPlaceholder}
                aria-label={ui.linkDescPlaceholder}
              />
              <button
                type="button"
                className="tool-icon-button"
                onClick={() => removeLink(index)}
                disabled={links.length <= 1}
                aria-label={ui.removeLink}
              >
                <Trash2 size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="ghost-button tool-add-link"
            onClick={addLink}
            disabled={links.length >= MAX_LINKS}
          >
            <Plus size={16} aria-hidden="true" />
            {ui.addLink}
          </button>
        </fieldset>
      </form>

      <div className="tool-output">
        <div className="tool-output-bar">
          <span className="tool-output-title">{ui.outputTitle}</span>
          <div className="tool-output-actions">
            <button type="button" className="ghost-button" onClick={copy}>
              {copied ? (
                <Check size={16} aria-hidden="true" />
              ) : (
                <Copy size={16} aria-hidden="true" />
              )}
              {copied ? ui.copied : ui.copy}
            </button>
            <button type="button" className="primary-button" onClick={download}>
              <Download size={16} aria-hidden="true" />
              {ui.download}
            </button>
          </div>
        </div>
        <pre className="tool-output-pre" aria-label={ui.outputTitle}>
          <code>{output}</code>
        </pre>
      </div>
    </div>
  )
}
